import { ENTITY_SCOPE_HEADER, entityScopeHeaderValue } from './entity-scope-header';

/**
 * The auth headers every authenticated request to the Connect API carries, for a given access
 * token: `Authorization: Bearer <token>` when there is a token, and the BC-7 entity-scope header
 * when `entityScopeHeaderValue` allows it for that token (DV-3421: the two must come from the same
 * token so they cannot disagree). Each transport decides what an absent header means for it: the
 * axios interceptor removes a stale X-Entity-UUID on a retried config but leaves Authorization to
 * its 401/403 retry, which sets it explicitly.
 *
 * The one place these rules live. Used by the RTK Query base query (api-base.ts), the legacy axios
 * interceptor (axios-interceptor.ts) and the upload modal's keepalive discard on page unload
 * (uploadAssetService.ts) — the next new header or rule change goes here, not in a transport.
 */
export const buildAuthHeaders = (
  token: string | null | undefined,
  { withEntityScope = true }: { withEntityScope?: boolean } = {},
): Record<string, string> => {
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (!withEntityScope) return headers;
  const entityScope = entityScopeHeaderValue(token);
  if (entityScope) headers[ENTITY_SCOPE_HEADER] = entityScope;
  return headers;
};

/**
 * Per-endpoint options read by `baseQueryWithAuth` (an RTK Query endpoint's
 * `extraOptions`).
 */
export interface AuthExtraOptions {
  /**
   * The endpoint asks about the caller's TOKEN, not about the account this tab
   * is showing, so it goes out with no `X-Entity-UUID` (DV-3421 review round
   * 5, item 3).
   *
   * <p>A switch calls these precisely when the tab's selection may be outside
   * what the token reaches. With the selection attached, the guard (which has
   * no path allow-list) refuses the very calls meant to rescue the tab. With
   * none, the guard answers `NO_SELECTION` and the backend reads the token,
   * which is the question asked.
   *
   * <p>A flag on the endpoint definition rather than a list of endpoint names
   * (review round 6, item H): a name list matched across every `createApi`, so
   * an unrelated endpoint that happened to share a name would silently lose
   * its header.
   */
  tokenOnly?: boolean;
  /**
   * A 403 from this endpoint is an answer, not a stale token: return it as is,
   * with no refresh-and-retry (a 401 still refreshes). DV-3500: the PMS health
   * probe (`POST …/pms/{entity}/test`) refuses an Internal Admin by design until
   * DV-3636, and fails open on any error, so a refresh could never change what
   * it shows — while each one writes the token and runs the reconciler in
   * every other open tab.
   */
  forbiddenIsFinal?: boolean;
}
